import {
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatCurrency,
    formatNumber,
    RadialChart,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ChartConfigItem,
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const stylesheet = StyleSheet.create({
    chart: {
        width: "100%"
    }
});

const NAME = "RadialChart";

const USAGE_CODE = `<Chart
    config={{
        used: {
            title: "Used",
            colorIndex: 1
        }
    }}
    description="Team space, 720 GB / 1 TB"
    title="Storage quota"
>
    <RadialChart
        centerTitle="Quota"
        variant="progress"
        valueKey="used"
        maxValue={1000}
        value={720}
    />
</Chart>`;

const GAUGE_CODE = `<RadialChart
    bands={[
        {
            title: "Normal",
            status: "success",
            to: 70
        },
        {
            title: "High",
            status: "warning",
            to: 85
        },
        {
            title: "Critical",
            status: "danger",
            to: 100
        }
    ]}
    variant="gauge"
    valueKey="load"
    maxValue={100}
    value={64}
/>`;

const LOADS = [
    {
        titleKey: "radialChartPage.loadNow",
        key: "now",
        value: 64
    },
    {
        titleKey: "radialChartPage.loadAverage",
        key: "average",
        value: 72
    },
    {
        titleKey: "radialChartPage.loadPeak",
        key: "peak",
        value: 91
    }
] as const;

const PAYMENTS = [
    {
        titleKey: "radialChartPage.payCredit",
        key: "credit",
        value: 46
    },
    {
        titleKey: "radialChartPage.payDebit",
        key: "debit",
        value: 24
    },
    {
        titleKey: "radialChartPage.payTransfer",
        key: "transfer",
        value: 14
    },
    {
        titleKey: "radialChartPage.payWallet",
        key: "wallet",
        value: 10
    },
    {
        titleKey: "radialChartPage.payCash",
        key: "cash",
        value: 6
    }
] as const;

const RANGES = [
    {
        titleKey: "radialChartPage.rangeWeek",
        key: "week",
        sales: [
            412000,
            538000,
            265000
        ],
        target: [
            710000,
            760000,
            600000
        ]
    },
    {
        titleKey: "radialChartPage.rangeMonth",
        key: "month",
        sales: [
            2460000,
            2950000,
            1620000
        ],
        target: [
            2800000,
            3200000,
            2400000
        ]
    },
    {
        titleKey: "radialChartPage.rangeQuarter",
        key: "quarter",
        sales: [
            2460000,
            2950000,
            1620000
        ],
        target: [
            8400000,
            9600000,
            7200000
        ]
    }
] as const;

const CURRENCIES = {
    tr: "TRY",
    en: "USD"
} as const;

const QUARTER = {
    previous: 3600000,
    target: 4800000,
    actual: 4200000
} as const;

const STORAGE_MAX = 1000;

const STORAGE_USED = 720;

const QUARTER_MAX = 5000000;

const SALES_MAX = 4000000;

const SALES_VALUE = 3120000;

const PERCENT = 100;

const GAUGE_MAX = 100;

const DEFAULT_LOAD = "now";

const DEFAULT_RANGE = "month";

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("radialChartPage.stackedTitle"),
        id: "stacked"
    },
    {
        title: t("radialChartPage.barsTitle"),
        id: "bars"
    },
    {
        title: t("radialChartPage.gaugeTitle"),
        id: "gauge"
    },
    {
        title: t("radialChartPage.centerTitle"),
        id: "center"
    },
    {
        title: t("radialChartPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const RadialChartPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const [
        load,
        setLoad
    ] = useState<string>(DEFAULT_LOAD);

    const [
        range,
        setRange
    ] = useState<string>(DEFAULT_RANGE);

    const formatMoney = (value: number) => formatCurrency({
        currency: CURRENCIES[language],
        options: {
            maximumFractionDigits: 2,
            notation: "compact"
        },
        locale: activeLocale,
        value
    });

    const formatPercent = (value: number, fractionDigits = 0) => formatNumber({
        options: {
            maximumFractionDigits: fractionDigits,
            style: "percent"
        },
        value: value / PERCENT,
        locale: activeLocale
    });

    const formatStorage = (value: number) => `${formatNumber({
        locale: activeLocale,
        value
    })} GB`;

    const activeLoad = LOADS.find((item) => item.key === load) ?? LOADS[0];

    const activeRange = RANGES.find((item) => item.key === range) ?? RANGES[1];

    const rangeRatios = activeRange.sales.map((sales, index) => Math.min(PERCENT, sales / (activeRange.target[index] ?? 1) * PERCENT));

    const channelKeys = [
        "web",
        "mobile",
        "store"
    ] as const;

    const rangeRows: Array<ChartDataRow> = channelKeys.map((key, index) => ({
        value: rangeRatios[index] ?? 0,
        name: key
    }));

    const rangeTotal = activeRange.sales.reduce((total, item) => total + item, 0) / activeRange.target.reduce((total, item) => total + item, 0) * PERCENT;

    const paymentRows: Array<ChartDataRow> = PAYMENTS.map((item) => ({
        value: item.value,
        name: item.key
    }));

    const quarterRows: Array<ChartDataRow> = [
        {
            value: QUARTER.target,
            name: "target"
        },
        {
            value: QUARTER.actual,
            name: "actual"
        },
        {
            value: QUARTER.previous,
            name: "previous"
        }
    ];

    const renderChart = (chart: ReactNode, props: {
        children?: ReactNode;
        config: ChartConfig;
        description: string;
        title: string;
        note?: string;
    }) => <View
        style={[
            stylesheet.chart,
            {
                maxWidth: shapeConfigs.contentMaxWidth * 2 / 3
            }
        ]}
    >
        <Chart
            description={props.description}
            spreadBehaviour="stretch"
            isAnimatedOnMount={true}
            config={props.config}
            title={props.title}
            note={props.note}
        >
            {props.children}
            {chart}
        </Chart>
    </View>;

    return <Article>
        <ComponentHeader
            lead={t("radialChartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("radialChartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("radialChartPage.overviewCaption")}
            >
                {
                    renderChart(<RadialChart
                        centerTitle={t("radialChartPage.storageCenter")}
                        valueFormatter={formatStorage}
                        maxValue={STORAGE_MAX}
                        value={STORAGE_USED}
                        variant="progress"
                        valueKey="used"
                    />, {
                        description: t("radialChartPage.storageDescription"),
                        config: {
                            used: {
                                title: t("radialChartPage.storageUsed"),
                                colorIndex: 1
                            }
                        },
                        title: t("radialChartPage.storageTitle"),
                        note: t("radialChartPage.storageNote")
                    })
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("radialChartPage.stackedTitle")}
            id="stacked"
        >
            <Paragraph>
                {t("radialChartPage.stackedText")}
            </Paragraph>
            <Preview
                caption={t("radialChartPage.stackedCaption")}
            >
                {
                    renderChart(<RadialChart
                        centerTitle={t("radialChartPage.quarterCenter", [
                            formatPercent(QUARTER.actual / QUARTER.target * PERCENT, 1)
                        ])}
                        categoryTitle={t("radialChartPage.quarterSeries")}
                        centerValue={formatMoney(QUARTER.actual)}
                        valueFormatter={formatMoney}
                        maxValue={QUARTER_MAX}
                        categoryKey="name"
                        data={quarterRows}
                        variant="stacked"
                        valueKey="value"
                    />, {
                        description: t("radialChartPage.quarterDescription"),
                        config: {
                            previous: {
                                title: t("radialChartPage.quarterPrevious"),
                                colorIndex: 3
                            },
                            actual: {
                                title: t("radialChartPage.quarterActual"),
                                colorIndex: 2
                            },
                            target: {
                                title: t("radialChartPage.quarterTarget"),
                                colorIndex: 1
                            }
                        },
                        title: t("radialChartPage.quarterTitle"),
                        note: t("radialChartPage.quarterNote")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radialChartPage.barsTitle")}
            id="bars"
        >
            <Paragraph>
                {t("radialChartPage.barsText")}
            </Paragraph>
            <Preview
                caption={t("radialChartPage.barsCaption")}
            >
                {
                    renderChart(<RadialChart
                        valueFormatter={(value) => formatPercent(value)}
                        categoryTitle={t("radialChartPage.payment")}
                        maxValue={PERCENT}
                        categoryKey="name"
                        data={paymentRows}
                        valueKey="value"
                        variant="bars"
                    />, {
                        config: Object.fromEntries(PAYMENTS.map((item, index) => [
                            item.key,
                            {
                                colorIndex: (index + 1) as ChartConfigItem["colorIndex"],
                                title: t(item.titleKey)
                            }
                        ])),
                        description: t("radialChartPage.paymentDescription"),
                        title: t("radialChartPage.paymentTitle"),
                        note: t("radialChartPage.paymentNote")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radialChartPage.gaugeTitle")}
            id="gauge"
        >
            <Paragraph>
                {t("radialChartPage.gaugeText")}
            </Paragraph>
            <Preview
                caption={t("radialChartPage.gaugeCaption")}
            >
                {
                    renderChart(<RadialChart
                        bands={[
                            {
                                title: t("radialChartPage.statusNormal"),
                                status: "success",
                                to: 70
                            },
                            {
                                title: t("radialChartPage.statusHigh"),
                                status: "warning",
                                to: 85
                            },
                            {
                                title: t("radialChartPage.statusCritical"),
                                status: "danger",
                                to: 100
                            }
                        ]}
                        valueFormatter={(value) => formatPercent(value)}
                        centerTitle={t(activeLoad.titleKey)}
                        value={activeLoad.value}
                        maxValue={GAUGE_MAX}
                        valueKey="load"
                        variant="gauge"
                    />, {
                        description: t("radialChartPage.loadDescription"),
                        config: {
                            load: {
                                title: t("radialChartPage.loadSeries"),
                                colorIndex: 1
                            }
                        },
                        title: t("radialChartPage.loadTitle"),
                        children: <Chart.Range
                            options={LOADS.map((item) => ({
                                title: t(item.titleKey),
                                key: item.key
                            }))}
                            onChange={setLoad}
                            value={load}
                        />,
                        note: t("radialChartPage.loadNote")
                    })
                }
            </Preview>
            <CodeBlock
                code={GAUGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("radialChartPage.centerTitle")}
            id="center"
        >
            <Paragraph>
                {t("radialChartPage.centerText")}
            </Paragraph>
            <Preview
                caption={t("radialChartPage.centerCaption")}
            >
                {
                    renderChart(<RadialChart
                        centerValue={formatPercent(SALES_VALUE / SALES_MAX * PERCENT)}
                        centerTitle={t("radialChartPage.goalCenter")}
                        valueFormatter={formatMoney}
                        maxValue={SALES_MAX}
                        value={SALES_VALUE}
                        variant="progress"
                        valueKey="sales"
                    />, {
                        description: t("radialChartPage.goalDescription"),
                        config: {
                            sales: {
                                title: t("radialChartPage.goalSales"),
                                colorIndex: 1
                            }
                        },
                        title: t("radialChartPage.goalTitle"),
                        note: t("radialChartPage.goalNote")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("radialChartPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("radialChartPage.rangeText")}
            </Paragraph>
            <Preview
                caption={t("radialChartPage.rangeCaption")}
            >
                {
                    renderChart(<RadialChart
                        valueFormatter={(value) => formatPercent(value, 1)}
                        centerTitle={t("radialChartPage.channelCenter")}
                        categoryTitle={t("radialChartPage.channel")}
                        centerValue={formatPercent(rangeTotal, 1)}
                        maxValue={PERCENT}
                        categoryKey="name"
                        variant="stacked"
                        data={rangeRows}
                        valueKey="value"
                    />, {
                        description: t("radialChartPage.channelDescription"),
                        config: {
                            mobile: {
                                title: t("radialChartPage.channelMobile"),
                                colorIndex: 2
                            },
                            store: {
                                title: t("radialChartPage.channelStore"),
                                colorIndex: 3
                            },
                            web: {
                                title: t("radialChartPage.channelWeb"),
                                colorIndex: 1
                            }
                        },
                        title: t("radialChartPage.channelTitle"),
                        children: <Chart.Range
                            options={RANGES.map((item) => ({
                                title: t(item.titleKey),
                                key: item.key
                            }))}
                            onChange={setRange}
                            value={range}
                        />
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("radialChartPage.a11yPlot"),
                    t("radialChartPage.a11yKeyboard"),
                    t("radialChartPage.a11yStatus"),
                    t("radialChartPage.a11yTable"),
                    t("radialChartPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"progress\" | \"stacked\" | \"bars\" | \"gauge\"",
                        description: t("radialChartPage.propVariant"),
                        defaultValue: "\"progress\"",
                        name: "variant"
                    },
                    {
                        description: t("radialChartPage.propValue"),
                        name: "value",
                        type: "number"
                    },
                    {
                        description: t("radialChartPage.propMaxValue"),
                        name: "maxValue",
                        type: "number"
                    },
                    {
                        type: "Array<Record<string, number | string>>",
                        description: t("radialChartPage.propData"),
                        name: "data"
                    },
                    {
                        description: t("radialChartPage.propCategoryKey"),
                        defaultValue: "\"category\"",
                        name: "categoryKey",
                        type: "string"
                    },
                    {
                        description: t("radialChartPage.propValueKey"),
                        defaultValue: "\"value\"",
                        name: "valueKey",
                        type: "string"
                    },
                    {
                        type: "Array<{ to: number; status: \"success\" | \"warning\" | \"danger\"; title: string }>",
                        description: t("radialChartPage.propBands"),
                        name: "bands"
                    },
                    {
                        description: t("radialChartPage.propCenterTitle"),
                        name: "centerTitle",
                        type: "string"
                    },
                    {
                        description: t("radialChartPage.propCenterValue"),
                        name: "centerValue",
                        type: "string"
                    },
                    {
                        description: t("radialChartPage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        description: t("radialChartPage.propCategoryTitle"),
                        name: "categoryTitle",
                        type: "string"
                    },
                    {
                        description: t("radialChartPage.propPlotSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "plotSize"
                    },
                    {
                        description: t("radialChartPage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        description: t("radialChartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        type: "boolean"
                    },
                    {
                        description: t("radialChartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour"
                    },
                    {
                        description: t("radialChartPage.propRenderTooltip"),
                        type: "(content: ChartTooltipContent) => ReactNode",
                        name: "renderTooltip"
                    },
                    {
                        description: t("radialChartPage.propOnActiveChange"),
                        type: "(index: number | undefined) => void",
                        name: "onActiveChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("radialChartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("radialChartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("radialChartPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default RadialChartPage;
